import { useState } from 'react'
import { courrierRelance, rapprochement411, tiers, type CompteTiers } from '../../api/compta'
import { dateCourte, fcfa } from '../Format'
import { Modale } from '../Modale'
import { Carte, exporterCSV, type PropsCompta } from './briques'

/**
 * Tiers — auxiliaire clients 411, lettrage et relances (transposition de
 * `renderTiers` / `opRelance` / `exportTiers`), depuis les VRAIES factures.
 * Le total de l'auxiliaire est rapproché du solde du compte 411 : un écart
 * signale une écriture manuelle sur le 411 qui ne correspond à aucune facture.
 */
export function Tiers({ donnees, soldes, surNotifier }: PropsCompta) {
  const liste = tiers(donnees)
  const r = rapprochement411(liste, soldes)
  const ok = Math.abs(r.ecart) < 1
  const [relance, setRelance] = useState<CompteTiers | null>(null)
  const totFact = liste.reduce((s, c) => s + c.facture, 0)
  const totReg = liste.reduce((s, c) => s + c.regle, 0)

  return (
    <>
      <Carte
        titre="Comptes clients — auxiliaire 411"
        icone="wallet"
        sous={
          <>
            {liste.length} client{liste.length > 1 ? 's' : ''} ·{' '}
            {ok ? (
              <span style={{ color: 'var(--green)', fontWeight: 700 }}>auxiliaire rapproché du compte 411 ({fcfa(r.compte)})</span>
            ) : (
              <span style={{ color: 'var(--err)', fontWeight: 700 }}>
                écart de {fcfa(Math.abs(r.ecart))} avec le compte 411 ({fcfa(r.compte)}) — une écriture manuelle touche le 411 sans facture
              </span>
            )}
          </>
        }
        surExport={() =>
          exporterCSV(
            'tiers-clients-411.csv',
            [['Client', 'Facturé TTC', 'Réglé', 'Solde ouvert', 'Ancienneté (j)', 'Factures ouvertes'], ...liste.map((c) => [c.client, c.facture, c.regle, c.ouvert, c.nbOuvertes ? c.plusAncienne : 0, c.nbOuvertes])],
            surNotifier,
          )
        }
      >
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th style={{ textAlign: 'right' }}>Facturé TTC</th>
                <th style={{ textAlign: 'right' }}>Réglé</th>
                <th style={{ textAlign: 'right' }}>Solde ouvert</th>
                <th style={{ textAlign: 'center' }}>Ancienneté</th>
                <th style={{ textAlign: 'center' }}>Ouvertes</th>
                <th style={{ width: 90 }}>Réglé</th>
                <th>Lettrage</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {liste.length === 0 && (
                <tr>
                  <td colSpan={9} className="t-muted">
                    Aucun client facturé — les factures émises alimentent cet auxiliaire.
                  </td>
                </tr>
              )}
              {liste.map((c) => {
                const pct = c.facture ? Math.round((c.regle / c.facture) * 100) : c.ouvert ? 0 : 100
                return (
                  <tr key={c.client}>
                    <td className="t-strong">{c.client}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{fcfa(c.facture)}</td>
                    <td className="mono num-c" style={{ textAlign: 'right' }}>{fcfa(c.regle)}</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{fcfa(c.ouvert)}</td>
                    <td style={{ textAlign: 'center' }}>{c.nbOuvertes ? `${c.plusAncienne} j` : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{c.nbOuvertes || '—'}</td>
                    <td>
                      <div className="rl-mini" role="img" aria-label={`${pct} % réglé`}>
                        <div className="rl-mini-f" style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                    <td>
                      {c.nbOuvertes === 0 ? (
                        <span className="badge b-green plain">Lettré</span>
                      ) : c.regle > 0 ? (
                        <span className="badge b-warn plain">Partiel</span>
                      ) : (
                        <span className="badge b-red plain">À lettrer</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {c.ouvert > 0 ? (
                        <button className="btn btn-ghost btn-sm" onClick={() => setRelance(c)}>
                          Relancer
                        </button>
                      ) : (
                        <span className="t-muted">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
              {liste.length > 0 && (
                <tr style={{ background: 'var(--card)' }}>
                  <td style={{ fontWeight: 800 }}>Total</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 800 }}>{fcfa(totFact)}</td>
                  <td className="mono num-c" style={{ textAlign: 'right', fontWeight: 800 }}>{fcfa(totReg)}</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 800 }}>{fcfa(r.auxiliaire)}</td>
                  <td colSpan={5} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Carte>
      <div className="dash-note">
        Le solde ouvert regroupe les factures émises non réglées — les créances portées par le compte 411. La relance produit
        un courrier prêt à copier ; rien n’est envoyé automatiquement.
      </div>
      {relance && <ModaleRelance compte={relance} expediteur={donnees.org.brand_name || donnees.org.name} surNotifier={surNotifier} surFermer={() => setRelance(null)} />}
    </>
  )
}

function ModaleRelance({ compte, expediteur, surNotifier, surFermer }: { compte: CompteTiers; expediteur: string; surNotifier: (m: string) => void; surFermer: () => void }) {
  const [texte, setTexte] = useState(() => courrierRelance(compte, expediteur, fcfa, dateCourte))
  async function copier() {
    try {
      await navigator.clipboard.writeText(texte)
      surNotifier('Courrier copié')
    } catch {
      surNotifier('Copie impossible : sélectionnez le texte et copiez-le à la main')
    }
  }
  return (
    <Modale titre={`Relance — ${compte.client}`} ouverte surFermer={surFermer} surEnregistrer={() => void copier()} libelleEnregistrer="Copier le courrier">
      <div className="field">
        <label htmlFor="rel-txt">Courrier de relance</label>
        <textarea
          id="rel-txt"
          rows={14}
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          style={{ width: '100%', padding: 11, border: '1px solid var(--border)', borderRadius: 11, fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, resize: 'vertical' }}
        />
      </div>
      <div className="dash-note">
        {fcfa(compte.ouvert)} dus sur {compte.nbOuvertes} facture{compte.nbOuvertes > 1 ? 's' : ''}, ancienneté maximale {compte.plusAncienne} j.
      </div>
    </Modale>
  )
}
